import { singleIndex } from 'instantsearch.js/es/lib/stateMappings';
import { type StateMapping, type UiState } from 'instantsearch.js';
import { stringify } from 'qs';

type RouteState = UiState & { filters?: string; searchFilters?: string | Array<string> };
type RefinementRouteState = Record<string, unknown> & { refinementList?: Record<string, Array<string>> };

// Wire format for a single facet filter in the URL: `attribute:value` (value may itself contain colons).
export const parseSearchFilter = (filter: string): [attribute: string, value: string] | null => {
  const [attribute, ...valueParts] = filter.split(':');
  const value = valueParts.join(':');

  return attribute && value ? [attribute, value] : null;
};

export const formatSearchFilter = (attribute: string, value: string): string => `${attribute}:${value}`;

export const toFilterArray = (searchFilters: string | Array<string> | undefined): Array<string> =>
  Array.isArray(searchFilters) ? searchFilters : searchFilters ? [searchFilters] : [];

export const createSearchUrl = (
  routeState: RouteState,
  location: Pick<Location, 'protocol' | 'hostname' | 'port' | 'pathname' | 'hash'>,
) => {
  const queryString = stringify(routeState, { arrayFormat: 'repeat' });
  const port = location.port ? `:${location.port}` : '';

  return `${location.protocol}//${location.hostname}${port}${location.pathname}${queryString ? `?${queryString}` : ''}${location.hash}`;
};

export const stateMapping: (index: string) => StateMapping<UiState, RouteState> = (index) => ({
  stateToRoute: (state) => {
    const { refinementList, ...route } = singleIndex(index).stateToRoute(state) as RefinementRouteState;

    return {
      ...route,
      // InstantSearch's searchBox connector drops an empty query from the UI state, so the router
      // would write a URL without `?query=`. Search mode is derived from that param (see
      // useIsSearching), so an empty search would immediately deactivate and "toggle" on/off.
      // Always persist `query` to keep an empty search active.
      query: state[index]?.query ?? '',
      ...(refinementList && {
        searchFilters: Object.entries(refinementList).flatMap(([attribute, values]) =>
          values.map((value) => formatSearchFilter(attribute, value)),
        ),
      }),
      ...(state[index]?.configure?.filters && { filters: state[index]?.configure?.filters }),
    };
  },

  routeToState: (route) => {
    const { filters, searchFilters, ...routeWithoutFilters } = route;
    const refinementList = toFilterArray(searchFilters).reduce<Record<string, Array<string>>>((refinements, filter) => {
      const parsed = parseSearchFilter(filter);

      if (parsed) {
        const [attribute, value] = parsed;
        (refinements[attribute] ??= []).push(value);
      }

      return refinements;
    }, {});

    return {
      [index]: {
        ...singleIndex(index).routeToState(routeWithoutFilters as UiState)[index],
        ...(Object.keys(refinementList).length > 0 && { refinementList }),
        ...(filters && { configure: { filters } }),
      },
    };
  },
});
